import type { AIPurchaseCoreMetrics } from '../../../types/api.types';
import {
    formatAdoptionPercent,
    formatCount,
    getAdoptionBadgeClasses,
    getAdoptionLevelLabel,
} from './aiPurchaseAnalyticsUtils';

interface OverallSummaryCardProps {
    title: string;
    description: string;
    overall: AIPurchaseCoreMetrics | null;
    loading: boolean;
    error: string | null;
}

/**
 * Cumulative AI purchase adoption summary — the prominent "hero" card at the top of
 * every AI Purchase Numbers page (overall / per-state / per-brand), with a
 * scope-appropriate title/description passed in by the page. Uses the same light-card
 * visual language as the now-approved `StateBreakdownGrid` tiles (white background,
 * dark legible text, coloured accent) rather than a dark gradient, so every label,
 * number, and badge is clearly legible — the "hero" feel comes from a teal top accent
 * bar, bolder/larger typography, and brand-tinted inner metric boxes, not from a dark
 * background. The two adoption percentages are the headline stat, shown side by side
 * with raw counts alongside so a high % on tiny volume never reads the same as a
 * lower % on huge volume (plan doc §2.7/§7.3). The red/amber/green adoption badges
 * are unchanged — they already have strong contrast on a light background.
 */
export default function OverallSummaryCard({
    title,
    description,
    overall,
    loading,
    error,
}: OverallSummaryCardProps) {
    return (
        <div className="rounded-2xl bg-white border border-[#e2e8f0] border-t-4 border-t-[#14b8a6] shadow-sm p-8">
            <h3 className="text-2xl font-bold text-[#1e293b]">{title}</h3>
            <p className="text-sm text-[#64748b] mt-1">{description}</p>

            {loading && (
                <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 border-2 border-[#14b8a6] border-t-transparent rounded-full animate-spin" />
                </div>
            )}

            {!loading && error && (
                <p className="text-sm text-[#dc2626] text-center py-6">{error}</p>
            )}

            {!loading && !error && overall && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <AdoptionHeadline
                        label="Adoption by purchases"
                        tooltip="% of active purchases/invoices that used AI extraction — are stores even trying AI on a given invoice?"
                        percent={overall.adoptionPercentByPurchases}
                        denominator={overall.totalActivePurchases}
                        linkedCount={overall.aiLinkedPurchases}
                        totalCount={overall.totalActivePurchases}
                        countLabel="active purchases"
                    />
                    <AdoptionHeadline
                        label="Adoption by line items"
                        tooltip="% of line items covered by AI-extracted purchases — how much manual-entry effort is actually being avoided or exposed?"
                        percent={overall.adoptionPercentByLineItems}
                        denominator={overall.totalLineItems}
                        linkedCount={overall.aiLinkedLineItems}
                        totalCount={overall.totalLineItems}
                        countLabel="line items"
                    />
                </div>
            )}

            {!loading && !error && !overall && (
                <p className="text-sm text-[#64748b] text-center py-6">
                    No AI purchase data available for this date range.
                </p>
            )}
        </div>
    );
}

interface AdoptionHeadlineProps {
    label: string;
    tooltip: string;
    percent: number;
    denominator: number;
    linkedCount: number;
    totalCount: number;
    countLabel: string;
}

function AdoptionHeadline({
    label,
    tooltip,
    percent,
    denominator,
    linkedCount,
    totalCount,
    countLabel,
}: AdoptionHeadlineProps) {
    return (
        <div className="rounded-xl bg-[#f0fdfa] border border-[#99f6e4] p-5" title={tooltip}>
            <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wide text-[#0f766e]">
                    {label}
                </p>
                <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${getAdoptionBadgeClasses(
                        percent,
                        denominator
                    )}`}
                >
                    {getAdoptionLevelLabel(percent, denominator)}
                </span>
            </div>
            <p className="text-5xl md:text-6xl font-bold text-[#0f766e] mt-1">
                {formatAdoptionPercent(percent, denominator)}
            </p>
            <p className="text-sm mt-2 text-[#475569]">
                {formatCount(linkedCount)} of {formatCount(totalCount)} {countLabel}
            </p>
        </div>
    );
}
